import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useListProductsQuery } from 'renderer/features/products-page/services/products.endpoints';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { dropdownKeyEventHandler } from 'renderer/shared/callbacks/keyEventHandler';
import { Discount } from '../schemas/discounts.schemas';
import {
  amountUnitsOptions,
  durationOptions,
  minimumUnitsOptions,
  submitButtonTextOptions,
} from '../constants/discounts.constants';
import {
  useCreateDiscountMutation,
  useUpdateDiscountMutation,
} from '../services/discounts.endpoints';

const initialDiscountForm: Discount = {
  code: '',
  name: '',
  amount: 0,
  amountUnits: 'dollars',
  start: moment().toISOString(),
  end: moment().add(1, 'day').toISOString(),
  duration: 'once',
  productIds: [],
  _id: '',
};

interface DiscountFormProps {
  mode: 'edit' | 'create';
  discount?: Discount | null;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const DiscountForm = ({ mode, discount, open, setOpen }: DiscountFormProps) => {
  const {
    data: products,
    isLoading: productsLoading,
    error: productsError,
  } = useListProductsQuery({ includeHidden: true });
  const [
    createDiscount,
    { isLoading: createLoading, error: createError, isSuccess: createSuccess },
  ] = useCreateDiscountMutation();
  const [
    updateDiscount,
    { isLoading: updateLoading, error: updateError, isSuccess: updateSuccess },
  ] = useUpdateDiscountMutation();

  const [discountForm, setDiscountForm] = useState(initialDiscountForm);

  useEffect(() => {
    if (mode === 'edit' && discount) {
      setDiscountForm(discount);
    }
  }, [discount, mode]);

  useEffect(() => {
    if (mode === 'create') {
      setDiscountForm(initialDiscountForm);
    }
  }, [mode]);

  const isLoading = productsLoading || createLoading || updateLoading;
  const error = mode === 'create' ? createError : updateError ?? productsError;
  const isSuccess = mode === 'create' ? createSuccess : updateSuccess;
  const submitButtonText = submitButtonTextOptions[mode];

  const getInputSafeOptionalValue = (value: string | number | undefined) =>
    value === undefined ? '' : value;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountForm({
      ...discountForm,
      [event.target.name]: event.target.value,
    });
  };

  const handleOptionalChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setDiscountForm({
      ...discountForm,
      [e.target.name]: e.target.value === '' ? undefined : e.target.value,
    });

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setDiscountForm({
      ...discountForm,
      [e.target.name]: e.target.valueAsNumber,
    });

  const handleOptionalNumberChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setDiscountForm({
      ...discountForm,
      [e.target.name]:
        e.target.value === '' ? undefined : e.target.valueAsNumber,
    });

  const handleSubmit = () => {
    if (mode === 'create') {
      createDiscount(discountForm);
    }
    if (mode === 'edit') {
      updateDiscount(discountForm);
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>Add a New Discount</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => setOpen(false)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <DialogContentText>
          Enter the details of the new discount.
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              autoFocus
              name="code"
              id="code"
              margin="dense"
              label="Code"
              fullWidth
              required
              disabled={isLoading}
              value={discountForm.code}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              autoFocus
              name="name"
              id="name"
              margin="dense"
              label="Brief Description"
              fullWidth
              required
              helperText="This will show on the purchase page when a discount is applied"
              disabled={isLoading}
              value={discountForm.name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              autoFocus
              name="amount"
              id="amount"
              margin="dense"
              label="Amount"
              type="number"
              fullWidth
              required
              disabled={isLoading || mode === 'edit'}
              value={discountForm.amount}
              onChange={handleNumberChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              autoFocus
              name="amountUnits"
              id="amountUnits"
              margin="dense"
              label="Units of Amount"
              select
              fullWidth
              required
              disabled={isLoading || mode === 'edit'}
              value={discountForm.amountUnits}
              onChange={handleChange}
            >
              {amountUnitsOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label="Discount Starts"
              name="starts"
              // disabled={isLoading}
              slotProps={{
                textField: { required: true },
              }}
              disabled={isLoading}
              value={dayjs(discountForm.start)}
              onChange={(start) => {
                setDiscountForm({
                  ...discountForm,
                  start: start ? start.toISOString() : '',
                });
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label="Discount Ends"
              name="ends"
              // disabled={isLoading}
              slotProps={{
                textField: { required: true },
              }}
              disabled={isLoading}
              value={dayjs(discountForm.end)}
              onChange={(end) => {
                setDiscountForm({
                  ...discountForm,
                  end: end ? end.toISOString() : '',
                });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              autoFocus
              name="duration"
              id="duration"
              margin="dense"
              label="Duration, Once Applied"
              helperText="For subscriptions, sets how long the discounted price applies. Irrelevant for one-time purchases."
              select
              fullWidth
              disabled={isLoading || mode === 'edit'}
              value={getInputSafeOptionalValue(discountForm.duration)}
              onChange={handleOptionalChange}
              onKeyUp={dropdownKeyEventHandler(durationOptions, (option) =>
                setDiscountForm({
                  ...discountForm,
                  duration: option as 'once' | 'forever',
                }),
              )}
            >
              {durationOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              autoFocus
              name="minimumUnits"
              id="minimumUnits"
              margin="dense"
              label="Minimum Units"
              select
              fullWidth
              disabled={isLoading}
              value={getInputSafeOptionalValue(discountForm.minimumUnits)}
              onChange={handleOptionalChange}
            >
              {minimumUnitsOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              autoFocus
              name="minimum"
              id="minimum"
              margin="dense"
              label="Minimum Amount"
              type="number"
              fullWidth
              disabled={isLoading || discountForm.minimumUnits === undefined}
              value={getInputSafeOptionalValue(discountForm.minimum)}
              onChange={handleOptionalNumberChange}
            />
          </Grid>
          {products && (
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="productIds"
                id="productIds"
                margin="dense"
                label="Products"
                select
                SelectProps={{ multiple: true }}
                fullWidth
                helperText="Leaving this field empty will allow this discount on all products"
                disabled={isLoading}
                value={discountForm.productIds}
                onChange={handleChange}
              >
                {products.map(({ _id, name }) => (
                  <MenuItem key={_id} value={_id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">
                Error creating discount: {formatQueryError(error)}
              </Alert>
            </Grid>
          )}
          {isSuccess && (
            <Grid item xs={12}>
              <Alert severity="success">
                {mode === 'create' ? 'Discount created!' : 'Discount edited!'}
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button disabled={isLoading} onClick={handleSubmit}>
          {isLoading ? <CircularProgress /> : submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscountForm;
